import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Star, Clock, Scale } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReview = async () => {
      try {
        const response = await axios.get(`${API}/reviews/${id}`);
        setReview(response.data);
      } catch (error) {
        console.error('Failed to fetch review:', error);
        navigate('/reviews');
      } finally {
        setLoading(false);
      }
    };
    fetchReview();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F2E8] pt-24 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="h-96 bg-white skeleton"></div>
        </div>
      </div>
    );
  }

  if (!review) return null;

  return (
    <div className="min-h-screen bg-[#F5F2E8] pt-24 pb-24">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#5A5A5A] hover:text-[#1A1A1A] mb-8 transition-colors text-sm font-['IBM_Plex_Sans']"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          BACK TO REPORTS
        </button>

        {/* Hero Image */}
        <div className="aspect-[21/9] overflow-hidden mb-8 border-2 border-[#E0DDD4]">
          <img 
            src={review.image}
            alt={review.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#4A7C29] bg-[#4A7C29]/10 px-3 py-1 font-['IBM_Plex_Sans_Condensed']">
            {review.category}
          </span>
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i} 
                className={`w-4 h-4 ${i < review.rating ? 'text-[#4A7C29] fill-current' : 'text-[#E0DDD4]'}`}
              />
            ))}
            <span className="text-sm text-[#5A5A5A] ml-1 font-['IBM_Plex_Mono']">{review.rating}/5</span>
          </div>
          <div className="flex items-center gap-1 text-sm text-[#5A5A5A] font-['IBM_Plex_Sans']">
            <Scale className="w-4 h-4" />
            <span>{review.weight_saved} saved</span>
          </div>
          <div className="flex items-center gap-1 text-sm text-[#5A5A5A] font-['IBM_Plex_Sans']">
            <Clock className="w-4 h-4" />
            <span>{review.date}</span>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4 leading-tight font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
          {review.title}
        </h1>

        {/* Author */}
        <div className="flex items-center gap-3 mb-8 pb-8 border-b-2 border-[#E0DDD4]">
          <div className="w-10 h-10 bg-[#4A7C29] flex items-center justify-center text-white font-semibold font-['IBM_Plex_Sans_Condensed']">
            {review.author[0]}
          </div>
          <div>
            <p className="font-semibold text-[#1A1A1A] font-['IBM_Plex_Sans']">{review.author}</p>
            <p className="text-xs text-[#5A5A5A] font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">FIELD TESTING PROGRAM</p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          {review.content.split('\n\n').map((paragraph, index) => {
            // Handle headers
            if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
              return (
                <h3 key={index} className="text-lg font-semibold text-[#1A1A1A] mt-8 mb-4 font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
                  {paragraph.replace(/\*\*/g, '')}
                </h3>
              );
            }
            
            // Handle lists
            if (paragraph.startsWith('- ')) {
              const items = paragraph.split('\n').filter(line => line.startsWith('- '));
              return (
                <ul key={index} className="list-disc list-inside space-y-2 my-4 text-[#5A5A5A] font-['IBM_Plex_Sans']">
                  {items.map((item, i) => (
                    <li key={i}>{item.replace('- ', '')}</li>
                  ))}
                </ul>
              );
            }

            // Handle italic sections
            if (paragraph.startsWith('*') && !paragraph.startsWith('**')) {
              return (
                <p key={index} className="text-[#5A5A5A] italic my-4 font-['IBM_Plex_Sans'] border-l-2 border-[#4A7C29] pl-4">
                  {paragraph.replace(/\*/g, '')}
                </p>
              );
            }

            // Regular paragraphs
            return (
              <p key={index} className="text-[#5A5A5A] leading-relaxed my-4 font-['IBM_Plex_Sans']">
                {paragraph.split('**').map((part, i) => 
                  i % 2 === 1 ? <strong key={i} className="text-[#1A1A1A]">{part}</strong> : part
                )}
              </p>
            );
          })}
        </div>

        {/* Share / More Reviews */}
        <div className="mt-12 pt-8 border-t-2 border-[#E0DDD4]">
          <h3 className="text-base font-semibold text-[#1A1A1A] mb-4 font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">ADDITIONAL REPORTS</h3>
          <Link 
            to="/reviews"
            className="inline-flex items-center gap-2 text-[#4A7C29] font-semibold hover:underline font-['IBM_Plex_Sans']"
          >
            View all evaluation reports
            <ArrowLeft className="w-4 h-4 rotate-180" />
          </Link>
        </div>
      </article>
    </div>
  );
};

export default ReviewDetail;
